import { ChangeEvent, ReactElement, useCallback, useEffect, useRef } from 'react'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import usePlanImageCardEditMode from '@editor/hooks/usePlanImageCardEditMode'
import usePlanImageCardMutations from '@editor/hooks/usePlanImageCardMutations'
import PlanExportConfigFields from '@editor/pages/StructuralPlanning/components/PlanTableForm/PlanExportConfigFields'
import { DeleteButton } from '@ui/actions'
import { usePlanImageCardStore } from '@editorStores'
import { useSelectedPlanElementId } from '@editorHooks'
import { DEFAULT_PLAN_IMAGE_CARD_WIDTH_CM } from 'src/domain/planImageCards/defaults'

const PlanImageCardForm = (): ReactElement => {
  const selectedId = useSelectedPlanElementId('image-card')
  const planImageCard = usePlanImageCardStore(state =>
    selectedId ? state.cards.find(card => card.id === selectedId) : undefined,
  )
  const placeholderCardIds = usePlanImageCardStore(state => state.placeholderCardIds)
  const isPlaceholder = planImageCard ? placeholderCardIds.includes(planImageCard.id) : false
  const {
    updatePlanImageCardFields,
    replacePlanImageCardAsset,
    deletePlanImageCard,
    flushPlanImageCardPersist,
    isUploadingAsset,
    isReplacingImageAsset,
  } = usePlanImageCardMutations()
  const { exitPlanImageCardEdit } = usePlanImageCardEditMode()
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    return () => flushPlanImageCardPersist()
  }, [planImageCard?.id, flushPlanImageCardPersist])

  const handleDelete = useCallback(() => {
    if (!planImageCard) return
    deletePlanImageCard(planImageCard.id)
    exitPlanImageCardEdit()
  }, [deletePlanImageCard, exitPlanImageCardEdit, planImageCard])

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file || !planImageCard) return
      await replacePlanImageCardAsset(planImageCard.id, file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [planImageCard, replacePlanImageCardAsset],
  )

  if (!planImageCard) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Kein Plan-Bild ausgewählt.
        </Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={1} p={1}>
      <Typography variant="h6" data-cy="plan-image-card-form-title">
        Plan-Bild bearbeiten
      </Typography>

      {isPlaceholder && (
        <Alert severity="warning" data-cy="plan-image-card-placeholder-alert">
          Noch kein Bild hochgeladen. Bitte ein Bild auswählen oder in die Szene einfügen.
        </Alert>
      )}

      <TextField
        label="Name"
        size="small"
        value={planImageCard.name}
        onChange={event =>
          updatePlanImageCardFields(planImageCard.id, { name: event.target.value })
        }
        onBlur={() => flushPlanImageCardPersist()}
        fullWidth
        data-cy="plan-image-card-name-input"
      />

      <TextField
        label="Titelzeilen"
        size="small"
        value={planImageCard.titleLines.join('\n')}
        onChange={event =>
          updatePlanImageCardFields(planImageCard.id, {
            titleLines: event.target.value.split('\n'),
          })
        }
        onBlur={() => flushPlanImageCardPersist()}
        multiline
        minRows={2}
        fullWidth
        helperText="Eine Zeile pro Titelzeile"
        data-cy="plan-image-card-title-input"
      />

      <TextField
        label="Breite (PDF cm)"
        size="small"
        type="number"
        value={planImageCard.width}
        onChange={event => {
          const width = Number(event.target.value)
          if (!Number.isFinite(width) || width <= 0) return
          updatePlanImageCardFields(planImageCard.id, { width })
        }}
        inputProps={{ min: 1, step: 1 }}
        fullWidth
        helperText={`Standard: ${DEFAULT_PLAN_IMAGE_CARD_WIDTH_CM} cm`}
        data-cy="plan-image-card-width-input"
      />

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          size="small"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          onClick={handleUploadClick}
          disabled={isUploadingAsset || isReplacingImageAsset}
          data-cy="plan-image-card-upload-button"
        >
          Bild hochladen
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          hidden
          onChange={handleFileChange}
          data-cy="plan-image-card-upload-input"
        />
      </Stack>

      <PlanExportConfigFields
        planConfig={planImageCard.planConfig}
        onChange={planConfig => updatePlanImageCardFields(planImageCard.id, { planConfig })}
      />

      <Stack direction="row" spacing={1}>
        <DeleteButton onClick={handleDelete} size="small" data-cy="plan-image-card-delete-button">
          Plan-Bild löschen
        </DeleteButton>
      </Stack>
    </Stack>
  )
}

export default PlanImageCardForm
